import { createFilterOptions, type FilterOptionsState } from '@mui/material';
import { useCallback, useState } from 'react';

import {
  useCreateDeliverableTypeMutation,
  useLazyGetDeliverableTypesQuery,
} from '@/features/deliverable-types/api/deliverable-typesApi';
import { AutocompleteAsync } from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import type {
  CreateDeliverableTypePayload,
  DeliverableType,
} from '@/features/deliverable-types/types';

type CreatedOption = {
  inputValue: string;
  label: string;
};

export type DeliverableTypeOption = {
  id: string;
  label: string;
};

type OptionType = DeliverableTypeOption | CreatedOption;

type DeliverableTypeSelectFieldProps = {
  value: DeliverableTypeOption | null;
  onChange: (value: DeliverableTypeOption | null) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  allowCreate?: boolean;
};

const filter = createFilterOptions<OptionType>();

const isCreatedOption = (option: unknown): option is CreatedOption =>
  typeof option === 'object' && option !== null && 'inputValue' in option;

const normalizeDeliverableTypes = (raw: unknown): DeliverableType[] => {
  if (Array.isArray(raw)) return raw as DeliverableType[];
  if (raw && typeof raw === 'object' && Array.isArray((raw as any).results)) {
    return (raw as any).results as DeliverableType[];
  }
  return [];
};

const getHasMore = (raw: unknown): boolean => {
  if (raw && typeof raw === 'object' && 'next' in raw) {
    return Boolean((raw as any).next);
  }
  return false;
};

export function DeliverableTypeSelectField({
  value,
  onChange,
  label = 'Deliverable Type',
  placeholder = 'Search deliverable types...',
  error,
  disabled,
  allowCreate = true,
}: DeliverableTypeSelectFieldProps) {
  const [createDeliverableType, { isLoading: isCreating }] =
    useCreateDeliverableTypeMutation();
  const [getDeliverableTypes] = useLazyGetDeliverableTypesQuery();
  const [inputValue, setInputValue] = useState('');

  const loadOptions = useCallback(
    async ({ query, page }: { query: string; page: number }) => {
      const params: { page: number; query?: string } = { page };
      if (query) params.query = query;

      const response = await getDeliverableTypes(params).unwrap();
      const deliverableTypes = normalizeDeliverableTypes(response.data);
      return {
        options: deliverableTypes.map((deliverableType) => ({
          id: deliverableType.id,
          label: deliverableType.label,
        })),
        hasMore: getHasMore(response.data),
      };
    },
    [getDeliverableTypes],
  );

  const filterOptions = (
    optionList: OptionType[],
    state: FilterOptionsState<OptionType>,
  ): OptionType[] => {
    const filtered = filter(optionList, state);
    const trimmedInput = state.inputValue.trim();

    if (!allowCreate || !trimmedInput) {
      return filtered;
    }

    const hasExisting = optionList.some(
      (option) =>
        !isCreatedOption(option) &&
        option.label.toLowerCase() === trimmedInput.toLowerCase(),
    );

    if (!hasExisting) {
      filtered.push({
        inputValue: trimmedInput,
        label: `Add "${trimmedInput}"`,
      });
    }

    return filtered;
  };

  const getOptionLabel = (option: OptionType) => {
    if (isCreatedOption(option)) return option.inputValue;
    return option.label;
  };

  const handleCreateDeliverableType = async (nextLabel: string) => {
    const payload: CreateDeliverableTypePayload = {
      label: nextLabel,
    };

    try {
      const response = await createDeliverableType(payload).unwrap();
      const created = response.data;
      if (!created) {
        notify({
          type: 'error',
          message: 'Unable to create deliverable type.',
        });
        return;
      }
      await getDeliverableTypes({
        page: 1,
        query: inputValue.trim() || undefined,
      });
      onChange({ id: created.id, label: created.label });
      notify({
        type: 'success',
        message: `Deliverable type "${created.label}" created.`,
      });
    } catch (err) {
      notify({ type: 'error', message: getErrorMessage(err) });
    }
  };

  const handleChange = (_event: unknown, nextValue: OptionType | null) => {
    if (!nextValue) {
      onChange(null);
      return;
    }

    if (isCreatedOption(nextValue)) {
      void handleCreateDeliverableType(nextValue.inputValue);
      return;
    }

    onChange(nextValue);
  };

  return (
    <AutocompleteAsync<OptionType>
      value={value}
      loadOptions={loadOptions}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, current) =>
        !isCreatedOption(option) && !isCreatedOption(current)
          ? option.id === current.id
          : false
      }
      filterOptions={filterOptions}
      inputValue={inputValue}
      onInputChange={(_event, nextValue) => setInputValue(nextValue)}
      onChange={handleChange}
      renderOption={(props, option) => {
        if (isCreatedOption(option)) {
          return <li {...props}>{option.label}</li>;
        }
        return <li {...props}>{option.label}</li>;
      }}
      noOptionsText="No deliverable types"
      loadingText="Loading deliverable types..."
      label={label}
      placeholder={placeholder}
      disabled={disabled || isCreating}
      textFieldProps={{
        error: Boolean(error),
        helperText: error,
      }}
    />
  );
}
